import { faFileSignature, faKey } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo } from 'react';

import CreateHealthieAccountChannel from '@/channels/CreateHealthieAccountChannel';
import HOSTS from '@/constants/hosts';
import {
  JOTFORM_REFERRAL_PRODUCTION,
  JOTFORM_REFERRAL_STAGING
} from '@/constants/jotformReferralLinks';
import CreateCandidPatientButton from '@/pages/Profile/components/OutlinksSection/components/CreateCandidPatientButton';
import CreateHealthieAccountButton from '@/pages/Profile/components/OutlinksSection/components/CreateHealthieAccountButton';
import { buttonVariants } from '@/ui';
import { CandidLogo } from '@/ui/components/ViewInCandid';
import { candidPatientUrl } from '@/utils/candidLink';
import { kustomerPersonLink } from '@/utils/kustomerLinks';

import { OutlinkLogo, OutlinksSplitButton } from './components';
import { Props } from './types';

const LINK_CLASSNAME = buttonVariants({
  className: 'px-3 pointer-coarse:h-11',
  color: 'secondary',
  size: 'md',
  variant: 'subtle'
});

const OutlinksSection = ({
  healthieUrl,
  homebaseUrl,
  host,
  iterableUrl,
  mixpanelUrl,
  kustomerId: personKustomerId,
  person,
  userId
}: Props) => {
  const { candid_patient_id: candidPatientId, id: personId } = person;

  return (
    <section className="flex flex-wrap gap-2">
      {healthieUrl && (
        <OutlinksSplitButton
          logo={<OutlinkLogo monochrome name="Healthie" src="/images/outlinks/healthie.svg" />}
          name="Healthie"
          outlinks={[
            { name: 'New Note', url: `${healthieUrl}/private_notes?new_note=true` },
            { name: 'Charting', url: `${healthieUrl}/private_notes` },
            { name: 'Billing', url: `${healthieUrl}/billing` },
            { name: 'Forms', url: `${healthieUrl}/intake_flow` },
            { name: 'Scheduling', url: `${healthieUrl.split('/users')[0]}/organization` }
          ]}
          url={healthieUrl}
        />
      )}

      <CreateHealthieAccountChannel personId={personId}>
        <CreateHealthieAccountButton person={person} />
      </CreateHealthieAccountChannel>

      {homebaseUrl && (
        <OutlinksSplitButton
          logo={<OutlinkLogo name="Homebase" src="/images/outlinks/homebase.svg" />}
          name="Homebase"
          outlinks={[{ name: 'Payments', url: `${host}/payments/${personId}/view` }]}
          url={homebaseUrl}
        />
      )}

      {personKustomerId && (
        <OutlinksSplitButton
          logo={<OutlinkLogo name="Kustomer" src="/images/outlinks/kustomer.svg" />}
          name="Kustomer"
          url={kustomerPersonLink({ host, personKustomerId })}
        />
      )}

      {candidPatientId ? (
        <OutlinksSplitButton
          logo={<CandidLogo size={14} />}
          name="Candid"
          url={candidPatientUrl(host, candidPatientId)}
        />
      ) : (
        <CreateCandidPatientButton person={person} />
      )}

      {iterableUrl && (
        <OutlinksSplitButton
          logo={<OutlinkLogo monochrome name="Iterable" src="/images/outlinks/iterable.png" />}
          name="Iterable"
          url={iterableUrl}
        />
      )}

      {mixpanelUrl && (
        <OutlinksSplitButton
          logo={<OutlinkLogo name="Mixpanel" src="/images/outlinks/mixpanel.svg" />}
          name="Mixpanel"
          url={mixpanelUrl}
        />
      )}

      <a
        className={LINK_CLASSNAME}
        href={host === HOSTS.production ? JOTFORM_REFERRAL_PRODUCTION : JOTFORM_REFERRAL_STAGING}
        rel="noreferrer"
        target="_blank">
        <FontAwesomeIcon icon={faFileSignature} />
        Referral Form
      </a>

      {userId && (
        <a
          className={LINK_CLASSNAME}
          href={`${host}/user/${userId}/user_set_password`}
          rel="noreferrer"
          target="_blank">
          <FontAwesomeIcon icon={faKey} />
          Reset Password
        </a>
      )}
    </section>
  );
};

export default memo(OutlinksSection);
